import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  ArrowLeft, TrendingUp, AlertTriangle, IndianRupee, Clock,
  RefreshCw, Building2, PieChart, Timer, AlertCircle, CheckCircle,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const BDAnalytics = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('ranking');
  const [period, setPeriod] = useState('fytd');
  
  // Data states
  const [builderRanking, setBuilderRanking] = useState(null);
  const [collectionAging, setCollectionAging] = useState(null);
  const [pipelineVelocity, setPipelineVelocity] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (activeTab === 'ranking') {
      fetchBuilderRanking();
    }
  }, [period]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchBuilderRanking(),
        fetchCollectionAging(),
        fetchPipelineVelocity()
      ]);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have access to BD Analytics');
        navigate('/bd');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchBuilderRanking = async () => {
    try {
      const res = await api.getBDBuilderRanking(period);
      setBuilderRanking(res.data);
    } catch (error) {
      console.error('Failed to load builder ranking:', error);
    }
  };

  const fetchCollectionAging = async () => {
    try {
      const res = await api.getBDCollectionAging();
      setCollectionAging(res.data);
    } catch (error) {
      console.error('Failed to load collection aging:', error);
    }
  };

  const fetchPipelineVelocity = async () => {
    try {
      const res = await api.getBDPipelineVelocity();
      setPipelineVelocity(res.data);
    } catch (error) {
      console.error('Failed to load pipeline velocity:', error);
    }
  };

  const formatCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(0)}K`;
    return `₹${amount?.toFixed(0) || 0}`;
  };

  const getRiskBadge = (level) => {
    const styles = {
      'High': 'bg-red-100 text-red-700',
      'Medium': 'bg-amber-100 text-amber-700',
      'Low': 'bg-emerald-100 text-emerald-700',
      'None': 'bg-slate-100 text-slate-500'
    };
    return <Badge className={styles[level] || styles['None']}>{level}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="animate-spin text-purple-600" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="bd-analytics-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/bd')} data-testid="bd-analytics-back-btn">
                <ArrowLeft size={18} />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-800">BD Analytics</h1>
                <p className="text-sm text-slate-500">Revenue, Collection & Pipeline Intelligence</p>
              </div>
            </div>
            <Button variant="outline" onClick={fetchAllData} data-testid="bd-analytics-refresh-btn">
              <RefreshCw size={16} className="mr-1" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="ranking" className="flex items-center gap-2">
              <TrendingUp size={16} />
              Builder Ranking
            </TabsTrigger>
            <TabsTrigger value="collection" className="flex items-center gap-2">
              <IndianRupee size={16} />
              Collection Aging
            </TabsTrigger>
            <TabsTrigger value="pipeline" className="flex items-center gap-2">
              <Timer size={16} />
              Pipeline Velocity
            </TabsTrigger>
          </TabsList>

          {/* Builder Ranking Tab */}
          <TabsContent value="ranking">
            <div className="space-y-6">
              {/* Period Selector & Concentration Risk */}
              <div className="flex flex-wrap gap-4 items-start justify-between">
                <div className="flex items-center gap-4">
                  <Select value={period} onValueChange={setPeriod}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mtd">Month to Date</SelectItem>
                      <SelectItem value="qtd">Quarter to Date</SelectItem>
                      <SelectItem value="fytd">FY to Date</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-slate-500">
                    {builderRanking?.total_builders || 0} builders with sales
                  </span>
                </div>

                {/* Concentration Risk Widget */}
                {builderRanking?.concentration_risk && (
                  <Card className="px-4 py-3">
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2">
                        <PieChart size={18} className="text-slate-500" />
                        <span className="text-sm font-medium text-slate-700">Concentration Risk</span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span>Top 5: <strong>{builderRanking.concentration_risk.top_5_pct}%</strong></span>
                        <span>Top 10: <strong>{builderRanking.concentration_risk.top_10_pct}%</strong></span>
                        {getRiskBadge(builderRanking.concentration_risk.risk_level)}
                      </div>
                    </div>
                  </Card>
                )}
              </div>

              {/* Total Revenue Card */}
              <Card className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600 font-medium">{builderRanking?.period || 'Period'} Revenue</p>
                    <p className="text-3xl font-bold text-purple-800">
                      {formatCurrency(builderRanking?.total_revenue || 0)}
                    </p>
                  </div>
                  <TrendingUp size={40} className="text-purple-300" />
                </div>
              </Card>

              {/* Builder Ranking Table */}
              <Card className="overflow-hidden">
                <div className="p-4 border-b bg-slate-50">
                  <h3 className="font-semibold text-slate-800">Builder Revenue Ranking</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">#</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Builder</th>
                        <th className="px-4 py-3 text-right font-medium text-slate-600">Bookings</th>
                        <th className="px-4 py-3 text-right font-medium text-slate-600">Net Sales</th>
                        <th className="px-4 py-3 text-right font-medium text-slate-600">Brokerage</th>
                        <th className="px-4 py-3 text-right font-medium text-slate-600">Collection %</th>
                        <th className="px-4 py-3 text-right font-medium text-slate-600">Avg Deal</th>
                        <th className="px-4 py-3 text-right font-medium text-slate-600">Cancel %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {builderRanking?.builders?.map((builder, idx) => (
                        <tr 
                          key={builder.builder_id} 
                          className="hover:bg-slate-50 cursor-pointer"
                          onClick={() => navigate(`/bd/builders/${builder.builder_id}`)}
                        >
                          <td className="px-4 py-3 text-slate-500">{idx + 1}</td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-slate-800">{builder.builder_name}</div>
                            <div className="text-xs text-slate-500">{builder.city} • {builder.active_projects} projects</div>
                          </td>
                          <td className="px-4 py-3 text-right font-medium">{builder.total_bookings}</td>
                          <td className="px-4 py-3 text-right font-medium text-emerald-600">
                            {formatCurrency(builder.total_sales)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div>{formatCurrency(builder.brokerage_expected)}</div>
                            <div className="text-xs text-slate-500">
                              Coll: {formatCurrency(builder.brokerage_collected)}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className={builder.collection_pct >= 80 ? 'text-emerald-600' : builder.collection_pct >= 50 ? 'text-amber-600' : 'text-red-600'}>
                              {builder.collection_pct}%
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">{formatCurrency(builder.avg_deal_size)}</td>
                          <td className="px-4 py-3 text-right">
                            <span className={builder.cancellation_rate > 10 ? 'text-red-600' : 'text-slate-600'}>
                              {builder.cancellation_rate}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Collection Aging Tab */}
          <TabsContent value="collection">
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card className="p-4">
                  <p className="text-sm text-slate-500">0-30 Days</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    {formatCurrency(collectionAging?.summary?.bucket_0_30 || 0)}
                  </p>
                </Card>
                <Card className="p-4">
                  <p className="text-sm text-slate-500">31-60 Days</p>
                  <p className="text-2xl font-bold text-amber-500">
                    {formatCurrency(collectionAging?.summary?.bucket_31_60 || 0)}
                  </p>
                </Card>
                <Card className="p-4">
                  <p className="text-sm text-slate-500">61-90 Days</p>
                  <p className="text-2xl font-bold text-orange-500">
                    {formatCurrency(collectionAging?.summary?.bucket_61_90 || 0)}
                  </p>
                </Card>
                <Card className="p-4 bg-red-50 border-red-200">
                  <p className="text-sm text-red-600">90+ Days</p>
                  <p className="text-2xl font-bold text-red-600">
                    {formatCurrency(collectionAging?.summary?.bucket_90_plus || 0)}
                  </p>
                </Card>
                <Card className="p-4 bg-slate-100">
                  <p className="text-sm text-slate-600">Total Outstanding</p>
                  <p className="text-2xl font-bold text-slate-800">
                    {formatCurrency(collectionAging?.summary?.total_outstanding || 0)}
                  </p>
                </Card>
              </div>

              {/* Risk Summary */}
              <div className="flex gap-4">
                <Badge className="bg-red-100 text-red-700 px-3 py-1">
                  🔴 {collectionAging?.summary?.high_risk_count || 0} High Risk Builders
                </Badge>
                <Badge className="bg-amber-100 text-amber-700 px-3 py-1">
                  🟡 {collectionAging?.summary?.medium_risk_count || 0} Medium Risk Builders
                </Badge>
              </div>

              {/* Aging Table */}
              <Card className="overflow-hidden">
                <div className="p-4 border-b bg-slate-50">
                  <h3 className="font-semibold text-slate-800">Outstanding by Builder</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Builder</th>
                        <th className="px-4 py-3 text-right font-medium text-slate-600">0-30d</th>
                        <th className="px-4 py-3 text-right font-medium text-slate-600">31-60d</th>
                        <th className="px-4 py-3 text-right font-medium text-slate-600">61-90d</th>
                        <th className="px-4 py-3 text-right font-medium text-slate-600">90+d</th>
                        <th className="px-4 py-3 text-right font-medium text-slate-600">Total</th>
                        <th className="px-4 py-3 text-center font-medium text-slate-600">Risk</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {collectionAging?.builders?.map((builder) => (
                        <tr key={builder.builder_id} className="hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <div className="font-medium text-slate-800">{builder.builder_name}</div>
                            <div className="text-xs text-slate-500">{builder.booking_count} bookings</div>
                          </td>
                          <td className="px-4 py-3 text-right text-emerald-600">
                            {formatCurrency(builder.bucket_0_30)}
                          </td>
                          <td className="px-4 py-3 text-right text-amber-500">
                            {formatCurrency(builder.bucket_31_60)}
                          </td>
                          <td className="px-4 py-3 text-right text-orange-500">
                            {formatCurrency(builder.bucket_61_90)}
                          </td>
                          <td className="px-4 py-3 text-right text-red-600">
                            {formatCurrency(builder.bucket_90_plus)}
                          </td>
                          <td className="px-4 py-3 text-right font-medium">
                            {formatCurrency(builder.total_outstanding)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {getRiskBadge(builder.risk_level)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Pipeline Velocity Tab */}
          <TabsContent value="pipeline">
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="p-4">
                  <p className="text-sm text-slate-500">In Pipeline</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {pipelineVelocity?.summary?.total_in_pipeline || 0}
                  </p>
                </Card>
                <Card className="p-4">
                  <p className="text-sm text-slate-500">Avg Days to Live</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {pipelineVelocity?.summary?.avg_days_to_live || 0}
                  </p>
                </Card>
                <Card className="p-4 bg-amber-50 border-amber-200">
                  <p className="text-sm text-amber-600">Stuck Projects</p>
                  <p className="text-2xl font-bold text-amber-600">
                    {pipelineVelocity?.summary?.stuck_count || 0}
                  </p>
                </Card>
                <Card className="p-4 bg-emerald-50 border-emerald-200">
                  <p className="text-sm text-emerald-600">Live Projects</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    {pipelineVelocity?.summary?.live_projects || 0}
                  </p>
                </Card>
              </div>

              {/* Stage Metrics */}
              <Card className="overflow-hidden">
                <div className="p-4 border-b bg-slate-50">
                  <h3 className="font-semibold text-slate-800">Stage Duration & Conversion</h3>
                </div>
                <div className="grid grid-cols-6 divide-x">
                  {pipelineVelocity?.stage_metrics?.map((stage, idx) => (
                    <div key={stage.stage} className="p-4 text-center">
                      <p className="text-xs font-medium text-slate-500 mb-2 truncate" title={stage.stage}>
                        {stage.stage.replace(' Meeting Done', '').replace(' Discussion', '')}
                      </p>
                      <p className="text-2xl font-bold text-slate-800">{stage.current_count}</p>
                      <div className="mt-2 space-y-1">
                        <div className="flex items-center justify-center gap-1 text-xs text-slate-500">
                          <Clock size={12} />
                          <span>{stage.avg_days}d avg</span>
                        </div>
                        {idx < 5 && (
                          <div className="text-xs">
                            <span className={stage.conversion_pct >= 50 ? 'text-emerald-600' : 'text-amber-600'}>
                              {stage.conversion_pct}% →
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Stuck Projects */}
              {pipelineVelocity?.stuck_projects?.length > 0 && (
                <Card className="overflow-hidden">
                  <div className="p-4 border-b bg-amber-50">
                    <div className="flex items-center gap-2">
                      <AlertTriangle size={18} className="text-amber-600" />
                      <h3 className="font-semibold text-amber-800">
                        Stuck Projects ({'>'}21 days in stage)
                      </h3>
                    </div>
                  </div>
                  <div className="divide-y">
                    {pipelineVelocity.stuck_projects.map((project) => (
                      <div 
                        key={project.bd_project_id}
                        className="flex items-center justify-between p-4 hover:bg-slate-50 cursor-pointer"
                        onClick={() => navigate(`/bd/projects/${project.bd_project_id}`)}
                      >
                        <div>
                          <p className="font-medium text-slate-800">{project.project_name}</p>
                          <p className="text-sm text-slate-500">{project.builder_name}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge variant="outline">{project.stage}</Badge>
                          <span className="text-red-600 font-medium">{project.days_stuck} days</span>
                          <ChevronRight size={16} className="text-slate-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default BDAnalytics;
